import React from 'react';
import { useDropzone, DropzoneProps, DropzoneState } from 'react-dropzone';
import { Datum, FetchState } from '../../../interfaces';
import { PropsFromRedux } from '.';
import { API_KEYS } from '../../../interfaces/api';

export interface DropZoneComponentProps extends DropzoneState {
  files: string[];
  imgFile : any;
  uploadState: FetchState;
  deleteFile: (e: React.MouseEvent<HTMLButtonElement>, url: string) => void;
}

interface Props extends DropzoneProps, PropsFromRedux {
  wrapperClassName?: string;
  labelClassName?: string;
  className?: string;
  label?: string;
  id: string;
  name: string;
  disabled?: boolean;
  helperText?: string;
  value?: any;
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  customChange?: Function;
  error?: Datum;
  fileProps?: string[];
  DropZoneComponent: React.FC<DropZoneComponentProps>;
}

const FileUpload: React.FC<Props> = ({
  wrapperClassName,
  labelClassName,
  label,
  id,
  value: valueProps,
  helperText,
  name,
  customChange,
  disabled,
  className,
  error,
  onChange,
  DropZoneComponent,
  multiple,
  uploadImage,
  uploadImageState,
  resetApi,
  fileProps,
  maxFiles,
  ...props
}) => {
  const [files, setFiles] = React.useState<string[]>(fileProps || []);
  const [imgFile, setImgFile] = React.useState<any>();
  const [errors, setErrors] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (!fileProps) {
      setFiles([]);
    }
  }, [fileProps]);

  React.useEffect(
    () => () => {
      resetApi(API_KEYS.UPLOAD_IMAGE);
    },
    [resetApi]
  );

  React.useEffect(() => {
    if (
      uploadImageState?.success &&
      !uploadImageState?.loading &&
      uploadImageState?.response?.data?.url
    ) {
      setFiles((prev) => [uploadImageState?.response?.data?.url, ...prev]);
      resetApi(API_KEYS.UPLOAD_IMAGE);
    }
  }, [
    uploadImageState?.loading,
    uploadImageState?.response?.data?.url,
    uploadImageState?.success,
    resetApi,
  ]);

    React.useEffect(() => {
      if(imgFile){
        setImgFile(files);
      }
  },[]);

  React.useEffect(() => {
    customChange && customChange(files);
  }, [customChange, files]);

  const onDrop = React.useCallback(
    (acceptedFiles, rejectedFiles) => {
      if (rejectedFiles?.length) {
        const newErrors = rejectedFiles[0]?.errors[0]?.map(
          (err: Error) => err?.message
        );
        setErrors([...newErrors, ...errors]);
      } else {
        acceptedFiles.forEach((file: any | Blob) => {
          uploadImage(file);
          // console.log('Image file --->',file);
          // setFiles((prev) => ['https://s3.eu-west-3.amazonaws.com/giglgoimages/GIGL/202007/1594923206114_GIGLogisticsprofilepicture16-07-2020-7-13-23-PM', ...prev]);
          // setImgFile(file);
          setImgFile(URL.createObjectURL(file));
        });
      }
      // console.log('Accepted file', acceptedFiles);
    },
    [errors, uploadImage]
  );

  const zoneState = useDropzone({
    onDrop,
    disabled: disabled || (!!maxFiles && files.length >= maxFiles),
    ...props,
  });

  const deleteFile = (
    e: React.MouseEvent<HTMLButtonElement>,
    fileUrl: string
  ) => {
    e.stopPropagation();
    const newFiles = files.filter((url) => fileUrl !== url);
    setFiles(newFiles);
    !!customChange && customChange(newFiles);
  };

  const { getInputProps, getRootProps } = zoneState;

  return (
    <div className={wrapperClassName}>
      {!!label && (
        <label className={labelClassName} htmlFor={id}>
          {label}
        </label>
      )}
      <div
        {...getRootProps({
          className,
        })}
      >
        <input {...getInputProps()} />
        <DropZoneComponent
          deleteFile={deleteFile}
          uploadState={uploadImageState}
          files={files}
          imgFile={imgFile}
          {...zoneState}
        />
      </div>
      {errors.map((fileInputError) => (
        <span
          key={fileInputError}
          className={`${
            error?.message ? 'text-red-600' : 'text-gray-600'
          } text-xs`}
        >
          {fileInputError}
        </span>
      ))}
      {error?.message && (
        <span
          className={`${
            error?.message ? 'text-red-600' : 'text-gray-600'
          } text-xs`}
        >
          {error?.message}
        </span>
      )}
    </div>
  );
};

export default FileUpload;
